import { useState, useEffect } from "react";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { format } from "date-fns";
import { ChevronDown, ChevronRight, Download } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  DuplicateAccountGroup,
  resolveTargetUser,
} from "@/pages/audit/types/duplicate-accounts";
import { useMergeDuplicateAccounts } from "../hooks/use-merge-duplicate-accounts";
import { MergeGroup } from "@/features/users/services/merge-duplicate-users";

function buildDocUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith("http")) return trimmed;
  return `https://docs.google.com/document/d/${trimmed}`;
}

interface Props {
  groups: DuplicateAccountGroup[];
  onUserClick: (userId: string) => void;
}

export function DuplicateAccountsTable({ groups, onUserClick }: Props) {
  // Selecting rows only leads to a fix, so view-only gets no checkbox column.
  const canWrite = useHasAccess("write");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  const { mutate: mergeGroups, isPending: isMerging } =
    useMergeDuplicateAccounts();

  useEffect(() => {
    setSelectedIds(new Set());
  }, [groups]);

  const allSelected =
    groups.length > 0 && groups.every((g) => selectedIds.has(g.otpless_identifier));
  const someSelected = selectedIds.size > 0;

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(groups.map((g) => g.otpless_identifier)));
    }
  };

  const selectedGroups = groups.filter((g) =>
    selectedIds.has(g.otpless_identifier)
  );

  const mergePayload: { group: DuplicateAccountGroup; target_user_id: string; source_user_ids: string[] }[] =
    selectedGroups.map((group) => {
      const target = resolveTargetUser(group.users);
      return {
        group,
        target_user_id: target.id,
        source_user_ids: group.users
          .filter((u) => u.id !== target.id)
          .map((u) => u.id),
      };
    });

  const toggleGroupExpanded = (id: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleExportCsv = () => {
    const headers = [
      "Identifier",
      "Source User",
      "Source Created",
      "Target User",
      "Bundles to Transfer",
      "Notes to Transfer",
      "Invoices to Transfer",
      "Prescriptions to Transfer",
      "Documents Transferred",
      "Document Conflicts",
    ];

    const rows = selectedGroups.flatMap((group) => {
      const target = resolveTargetUser(group.users);
      const targetDocKeys = new Set(Object.keys(target.document_ids ?? {}));
      const sources = group.users.filter((u) => u.id !== target.id);

      return sources.map((src) => {
        const srcDocs = src.document_ids ?? {};
        const targetDocs = target.document_ids ?? {};

        const transferred = Object.entries(srcDocs)
          .filter(([k]) => !targetDocKeys.has(k))
          .map(([k, v]) => `${k}: ${buildDocUrl(v)}`)
          .join("; ");

        const conflicts = Object.entries(srcDocs)
          .filter(([k]) => targetDocKeys.has(k))
          .map(([k, v]) => `${k} — source: ${buildDocUrl(v)} vs target: ${buildDocUrl(targetDocs[k])}`)
          .join("; ");

        return [
          group.otpless_identifier,
          src.full_name,
          format(new Date(src.created_at), "dd MMM yyyy HH:mm"),
          target.full_name,
          src.bundle_usage_count ?? 0,
          src.note_count ?? 0,
          src.invoice_count ?? 0,
          src.prescription_count ?? 0,
          transferred,
          conflicts,
        ];
      });
    });

    const csv = [headers, ...rows]
      .map((row) =>
        row.map((v) => `"${String(v).replace(/"/g, '""')}"`).join(",")
      )
      .join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `document-conflicts-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openConfirmDialog = () => {
    setExpandedGroups(new Set());
    setIsConfirmOpen(true);
  };

  const handleConfirmMerge = () => {
    const mergeGroups_payload: MergeGroup[] = mergePayload.map(
      ({ target_user_id, source_user_ids }) => ({
        target_user_id,
        source_user_ids,
      })
    );
    mergeGroups(mergeGroups_payload, {
      onSuccess: () => {
        handleExportCsv();
        setIsConfirmOpen(false);
        setExpandedGroups(new Set());
        setSelectedIds(new Set());
      },
    });
  };

  return (
    <>
      {/* Selection bar — only when rows are selected */}
      {someSelected && (
        <div className="flex items-center gap-3 px-2 py-2.5 mb-2 rounded-md bg-gray-50 border border-gray-200">
          <span className="text-xs font-medium text-gray-600">
            {selectedIds.size} selected
          </span>
          <button
            onClick={toggleAll}
            className="text-xs text-blue-600 hover:underline"
          >
            {allSelected ? "Deselect all" : "Select all"}
          </button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={handleExportCsv}
              className="gap-1.5"
            >
              <Download className="h-3.5 w-3.5" />
              Export CSV
            </Button>
            <HasAccess permission="write">
            <Button
              size="sm"
              onClick={openConfirmDialog}
              disabled={isMerging}
            >
              Merge
            </Button>
            </HasAccess>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {canWrite && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allSelected}
                  onCheckedChange={toggleAll}
                  disabled={groups.length === 0}
                />
              </TableHead>
            )}
            <TableHead>Identifier</TableHead>
            <TableHead>Accounts</TableHead>
            <TableHead>Users</TableHead>
            <TableHead className="text-center">Total Bundle Usages</TableHead>
            <TableHead>Detected Target</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {groups.map((group) => {
            const isSelected = selectedIds.has(group.otpless_identifier);
            const target = resolveTargetUser(group.users);
            const totalBundleUsages = group.users.reduce(
              (sum, u) => sum + u.bundle_usage_count,
              0
            );

            return (
              <TableRow
                key={group.otpless_identifier}
                className={cn(isSelected && "bg-blue-50/50")}
              >
                {canWrite && (
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(group.otpless_identifier)}
                    />
                  </TableCell>
                )}

                <TableCell className="font-mono text-xs text-muted-foreground max-w-[140px] truncate">
                  {group.otpless_identifier}
                </TableCell>

                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {group.user_count} accounts
                  </Badge>
                </TableCell>

                <TableCell className="max-w-[220px]">
                  <div className="flex flex-col gap-1">
                    {group.users.map((u) => (
                      <div key={u.id} className="flex flex-col gap-0.5">
                        <button
                          onClick={() => onUserClick(u.id)}
                          className="text-xs text-left text-blue-600 hover:underline hover:text-blue-800 transition-colors font-medium"
                        >
                          {u.full_name} - {format(new Date(u.created_at), "dd MMM yyyy, HH:mm")}
                        </button>
                      </div>
                    ))}
                  </div>
                </TableCell>

                <TableCell className="text-center tabular-nums text-sm">
                  {totalBundleUsages}
                </TableCell>

                <TableCell>
                  <div className="flex flex-col gap-0.5">
                    <button
                      onClick={() => onUserClick(target.id)}
                      className="text-xs text-left text-blue-600 hover:underline font-medium"
                    >
                      {target.full_name}
                    </button>
                    <div className="flex gap-1 flex-wrap mt-0.5">
                      {target.has_identifiers && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-green-50 text-green-800 border-green-200"
                        >
                          Has identifiers
                        </Badge>
                      )}
                      {target.bundle_usage_count > 0 && (
                        <Badge
                          variant="outline"
                          className="text-xs bg-blue-50 text-blue-800 border-blue-200"
                        >
                          {target.bundle_usage_count} bundles
                        </Badge>
                      )}
                    </div>
                  </div>
                </TableCell>

              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>Confirm Merge</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm text-gray-600">
            {/* Top summary */}
            <div>
              <p>
                You are about to merge{" "}
                <span className="font-semibold text-gray-900">
                  {mergePayload.length} group
                  {mergePayload.length !== 1 ? "s" : ""}
                </span>
                .
              </p>
              {(() => {
                const allSources = mergePayload.flatMap(({ source_user_ids, group }) =>
                  group.users.filter((u) => source_user_ids.includes(u.id))
                );
                const parts: string[] = [];
                const b = allSources.reduce((s, u) => s + (u.bundle_usage_count ?? 0), 0);
                const n = allSources.reduce((s, u) => s + (u.note_count ?? 0), 0);
                const inv = allSources.reduce((s, u) => s + (u.invoice_count ?? 0), 0);
                const pr = allSources.reduce((s, u) => s + (u.prescription_count ?? 0), 0);
                if (b) parts.push(`${b} bundle${b !== 1 ? "s" : ""}`);
                if (n) parts.push(`${n} note${n !== 1 ? "s" : ""}`);
                if (inv) parts.push(`${inv} invoice${inv !== 1 ? "s" : ""}`);
                if (pr) parts.push(`${pr} prescription${pr !== 1 ? "s" : ""}`);
                return parts.length > 0 ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Transferring: {parts.join(" · ")}
                  </p>
                ) : null;
              })()}
            </div>

            {/* Accordion list */}
            <div className="rounded-md border border-gray-200 divide-y divide-gray-100 max-h-[380px] overflow-y-auto">
              {mergePayload.map(({ group, target_user_id, source_user_ids }) => {
                const targetUser = group.users.find((u) => u.id === target_user_id)!;
                const sourceUsers = group.users.filter((u) =>
                  source_user_ids.includes(u.id)
                );
                const isExpanded = expandedGroups.has(group.otpless_identifier);
                const targetDocKeys = new Set(Object.keys(targetUser.document_ids ?? {}));

                // Detect any doc conflicts across all sources for the collapsed header hint
                const groupHasConflicts = sourceUsers.some((src) =>
                  Object.keys(src.document_ids ?? {}).some((k) => targetDocKeys.has(k))
                );

                const collapsedSources =
                  sourceUsers.length <= 2
                    ? sourceUsers.map((u) => u.full_name).join(" · ")
                    : `${sourceUsers.slice(0, 2).map((u) => u.full_name).join(" · ")} · +${sourceUsers.length - 2} more`;

                return (
                  <div key={group.otpless_identifier}>
                    {/* Collapsed header — always visible */}
                    <button
                      onClick={() => toggleGroupExpanded(group.otpless_identifier)}
                      className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors"
                    >
                      <span className="text-xs text-gray-700 truncate pr-2">
                        <span className="text-muted-foreground">{collapsedSources}</span>
                        <span className="mx-1.5 text-gray-400">→</span>
                        <span className="font-semibold text-gray-900">{targetUser.full_name}</span>
                        <span className="ml-1 text-green-700">✓</span>
                        {groupHasConflicts && (
                          <span className="ml-2 text-amber-600">⚠ doc conflict</span>
                        )}
                      </span>
                      {isExpanded ? (
                        <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                      )}
                    </button>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="px-3 pb-3 space-y-2 bg-gray-50 border-t border-gray-100">
                        {sourceUsers.map((src) => {
                          const srcDocs = src.document_ids ?? {};
                          const conflictKeys = Object.keys(srcDocs).filter((k) =>
                            targetDocKeys.has(k)
                          );
                          const newDocCount = Object.keys(srcDocs).filter(
                            (k) => !targetDocKeys.has(k)
                          ).length;

                          const parts: string[] = [];
                          if (src.bundle_usage_count) parts.push(`${src.bundle_usage_count} bundle${src.bundle_usage_count !== 1 ? "s" : ""}`);
                          if (src.note_count) parts.push(`${src.note_count} note${src.note_count !== 1 ? "s" : ""}`);
                          if (src.invoice_count) parts.push(`${src.invoice_count} invoice${src.invoice_count !== 1 ? "s" : ""}`);
                          if (src.prescription_count) parts.push(`${src.prescription_count} prescription${src.prescription_count !== 1 ? "s" : ""}`);
                          if (newDocCount) parts.push(`${newDocCount} new doc${newDocCount !== 1 ? "s" : ""}`);

                          return (
                            <div key={src.id} className="pt-2">
                              <div className="flex items-baseline justify-between gap-2">
                                <span className="text-xs font-medium text-gray-800">
                                  {src.full_name}
                                </span>
                                <span className="text-xs text-muted-foreground shrink-0">
                                  {format(new Date(src.created_at), "dd MMM yyyy")}
                                </span>
                              </div>
                              <p className={cn(
                                "text-xs mt-0.5",
                                parts.length ? "text-gray-600" : "text-gray-400 italic"
                              )}>
                                {parts.length ? parts.join(" · ") : "empty — no data to transfer"}
                              </p>

                              {/* Doc conflicts for this source */}
                              {conflictKeys.length > 0 && (
                                <div className="mt-1.5 rounded-md bg-amber-50 border border-amber-200 px-2.5 py-2 space-y-1">
                                  <p className="text-xs font-semibold text-amber-800">
                                    ⚠ {conflictKeys.length} doc conflict{conflictKeys.length > 1 ? "s" : ""} — manual review needed
                                  </p>
                                  {conflictKeys.map((key) => (
                                    <div key={key} className="text-xs text-amber-700 flex flex-wrap gap-x-1">
                                      <span className="font-medium">{key}:</span>
                                      <a
                                        href={buildDocUrl(srcDocs[key])}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline hover:text-amber-900"
                                      >
                                        source doc
                                      </a>
                                      <span className="text-amber-500">vs</span>
                                      <a
                                        href={buildDocUrl((targetUser.document_ids ?? {})[key])}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="underline hover:text-amber-900"
                                      >
                                        target doc
                                      </a>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}

                        <p className="text-xs text-amber-700 pt-2 border-t border-gray-200 mt-1">
                          ⊘ Identifier{sourceUsers.length > 1 ? "s" : ""} on{" "}
                          {sourceUsers.length === 1 ? "this account" : `${sourceUsers.length} accounts`}{" "}
                          will be nullified
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="text-amber-700 font-medium text-xs">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isMerging}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmMerge} disabled={isMerging}>
              {isMerging ? "Merging…" : "Confirm Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
